/*
 * Button
 * @module components
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { font, palette } from 'styled-theme';
import { ifProp } from 'styled-tools';
import { transparentize } from 'polished';
import * as R from 'ramda';

import { Loader } from '../Loader';

export type ButtonProps = {
  children?: React$Node,
  outlined?: boolean,
  disabled?: boolean,
  palette?: string,
  small?: boolean,
  loading?: boolean,
  selected?: boolean,
};

const backgroundColor = ({ outlined, disabled, selected }: ButtonProps) =>
  outlined ? 'transparent' : selected ? palette(3) : palette(disabled ? 3 : 1);
const foregroundColor = ({
  outlined,
  disabled,
  selected,
  palette: p,
}: ButtonProps) =>
  outlined
    ? palette(disabled ? 3 : 1)
    : selected
    ? palette('grayscale', 5)
    : palette('grayscale', 0, !R.equals('secondary', p));
const hoverBackgroundColor = ({ outlined, disabled, selected }: ButtonProps) =>
  selected
    ? !disabled && !outlined && '#555'
    : !disabled && !outlined && palette(0);
const hoverForegroundColor = ({ outlined, disabled }: ButtonProps) =>
  !disabled && outlined && palette(1);
const boxShadowColor = R.pipe(palette('shadow', 0), transparentize(0.8));

const styles = css`
  display: inline-flex;
  padding: 0 1em;
  min-width: 120px;
  height: ${ifProp('small', '40px', '50px')};
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  border: 1px solid ${ifProp('outlined', 'currentcolor', 'transparent')};
  border-radius: ${ifProp('small', '20px', '25px')};
  background-color: ${backgroundColor};
  color: ${foregroundColor};
  font-family: ${font('primary')};
  font-size: ${ifProp('small', '15px', '17px')};
  font-weight: bold;
  text-decoration: none;
  cursor: ${ifProp('disabled', 'default', 'pointer')};
  appearance: none;
  text-transform: uppercase;
  pointer-events: ${ifProp('disabled', 'none', 'auto')};
  transition: all 250ms ease-out;
  ${ifProp(
    'shadowed',
    css`
      box-shadow: 0 20px 30px 0 ${boxShadowColor};
    `,
  )};

  &:hover,
  &:focus,
  &:active {
    background-color: ${hoverBackgroundColor};
    color: ${hoverForegroundColor};
  }
  &:focus {
    outline: none;
  }

  ${/* sc-selector */ Loader} {
    color: currentColor;
  }
`;

export const StyledButton = styled.button.attrs(() => ({
  testid: 'styled-button',
  role: 'button',
}))`
  ${styles};
`;

/**
 * Button React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React.Children} [props.children] - childs
 * @param {boolean} [props.outlined] - transparent button with border
 * @param {boolean} [props.disabled] - disable button state
 * @param {boolean} [props.small] - small button size
 * @param {boolean} [props.loading] - loading state
 * @param {string} [props.palette = 'primary'] - current button palette
 * @example <caption>Simple usage</caption>
 * <Button>Button</Button>
 * @returns {React.Node<*>} React-component
 */
export const Button = ({
  children,
  small,
  disabled,
  loading,
  ...rest
}: ButtonProps) => (
  <StyledButton
    small={small}
    {...rest}
    disabled={disabled || loading}
    {...rest}
  >
    {loading ? <Loader size={small ? 'small' : 'medium'} /> : children}
  </StyledButton>
);

Button.defaultProps = {
  palette: 'primary',
  testid: 'button',
};
